import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoListListResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface UseTodoListReorderReturn {
  /** Persist a new dashboard order (the full set of visible list ids). */
  reorder: (order: string[]) => Promise<unknown>;
  reordering: boolean;
}

/**
 * Optimistic drag-reorder of the Holder's list dashboard. Reorders the cached
 * `todoLists.my` list immediately, persists the new sequence via the
 * `reorder_lists` verb, and rolls back + toasts on failure. Owner/admin/self
 * only — the server re-enforces the role, so a denied reorder simply reverts.
 *
 * Ordering is a workspace-shared attribute (mirrors sections/items), so success
 * invalidates the query to reconcile against the server's canonical sort_order.
 */
export function useTodoListReorder(holderId: string | null): UseTodoListReorderReturn {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const myKey = queryKeys.todoLists.my(holderId ?? '');

  const mut = useMutation({
    mutationFn: (order: string[]) => api.reorderTodoLists(holderId as string, order),
    onMutate: async (order: string[]) => {
      await queryClient.cancelQueries({ queryKey: myKey });
      const previous = queryClient.getQueryData<TodoListListResponse>(myKey);
      // Reorder the cached lists to match `order`; assign the same dense
      // sort_order the server will compute so a later render stays stable.
      queryClient.setQueryData<TodoListListResponse>(myKey, (old) => {
        if (!old) return old;
        const pos = new Map(order.map((id, idx) => [id, idx]));
        const lists = [...old.lists]
          .map((l) => (pos.has(l.listId) ? { ...l, sortOrder: pos.get(l.listId)! } : l))
          .sort((a, b) => a.sortOrder - b.sortOrder);
        return { ...old, lists };
      });
      return { previous };
    },
    onError: (_err, _order, context) => {
      if (context?.previous) queryClient.setQueryData(myKey, context.previous);
      toast({ title: 'Couldn’t reorder your lists', variant: 'destructive' });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: myKey });
    },
  });

  return {
    reorder: (order: string[]) => mut.mutateAsync(order),
    reordering: mut.isPending,
  };
}
